import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Pending', bg: 'rgba(198,169,107,0.15)', color: '#8B6B28' },
  reviewed: { label: 'Reviewed', bg: 'rgba(168,181,162,0.2)', color: '#4A5E45' },
  accepted: { label: 'Accepted', bg: 'rgba(100,160,100,0.15)', color: '#2E6B2E' },
  declined: { label: 'Declined', bg: 'rgba(180,80,80,0.12)', color: '#8B2020' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span
      className="text-xs font-medium px-3 py-1 rounded-full"
      style={{ background: cfg.bg, color: cfg.color, fontFamily: 'Inter, sans-serif' }}
    >
      {cfg.label}
    </span>
  );
};

const Field = ({ label, value }) => (
  <div className="mb-6">
    <p className="text-xs tracking-[0.15em] uppercase font-medium mb-1" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
      {label}
    </p>
    <p className="text-sm leading-relaxed" style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif', whiteSpace: 'pre-wrap' }}>
      {value || '—'}
    </p>
  </div>
);

const AdminDashboard = () => {
  const location = useLocation();
  const [token, setToken] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [sheetsStatus, setSheetsStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchApplications = useCallback(async (currentToken) => {
    if (!currentToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/applications`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      setApplications(res.data);
    } catch (err) {
      if (err.response?.status === 401) setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSheetsStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/oauth/sheets/status`);
      setSheetsStatus(res.data);
    } catch {
      // silently ignore — sheets status is non-critical
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchApplications(token);
      fetchSheetsStatus();
    }
  }, [token, fetchApplications, fetchSheetsStatus]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('sheets') === 'connected') {
      setStatusMsg('Google Sheets connected successfully!');
      fetchSheetsStatus();
      setTimeout(() => setStatusMsg(''), 5000);
    }
  }, [location.search, fetchSheetsStatus]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await axios.post(`${API}/admin/login`, loginForm);
      setToken(res.data.token);
    } catch {
      setLoginError('Invalid credentials. Please try again.');
    }
  };

  const handleLogout = () => {
    setToken(null);
    setApplications([]);
    setSelectedApp(null);
  };

  const handleStatusUpdate = async (appId, newStatus) => {
    try {
      await axios.patch(
        `${API}/applications/${appId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApplications((prev) =>
        prev.map((a) => (a.id === appId ? { ...a, status: newStatus } : a))
      );
      if (selectedApp?.id === appId) {
        setSelectedApp((prev) => ({ ...prev, status: newStatus }));
      }
    } catch {
      setStatusMsg('Failed to update status. Please try again.');
      setTimeout(() => setStatusMsg(''), 4000);
    }
  };

  const handleDelete = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/applications/${appId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApplications((prev) => prev.filter((a) => a.id !== appId));
      if (selectedApp?.id === appId) setSelectedApp(null);
    } catch {
      setStatusMsg('Failed to delete application. Please try again.');
      setTimeout(() => setStatusMsg(''), 4000);
    }
  };

  const handleInitHeaders = async () => {
    try {
      await axios.post(`${API}/sheets/init-headers`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatusMsg('Sheet headers initialized successfully!');
      setTimeout(() => setStatusMsg(''), 5000);
    } catch {
      setStatusMsg('Failed to initialize headers. Make sure Google Sheets is connected.');
      setTimeout(() => setStatusMsg(''), 5000);
    }
  };

  const filteredApps = applications.filter((a) => {
    const q = searchQuery.toLowerCase();
    return !q || a.fullName?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q);
  });

  const stats = {
    total: applications.length,
    pending: applications.filter((a) => a.status === 'pending').length,
    reviewed: applications.filter((a) => a.status === 'reviewed').length,
    accepted: applications.filter((a) => a.status === 'accepted').length,
  };

  // ── LOGIN ──────────────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div
        data-testid="admin-login-page"
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: '#F7F4EF', paddingTop: '80px' }}
      >
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <p className="text-xs tracking-[0.3em] uppercase font-medium mb-3" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
              Resonant Healing
            </p>
            <h1 className="font-serif text-3xl" style={{ color: '#2B2B2B' }}>Admin Access</h1>
          </div>

          <form
            onSubmit={handleLogin}
            data-testid="admin-login-form"
            className="p-10 rounded-3xl"
            style={{ background: '#EAE3D9', boxShadow: '0 16px 56px rgba(126,140,116,0.1)' }}
          >
            {loginError && (
              <div
                data-testid="login-error"
                className="mb-6 text-sm p-3 rounded-xl"
                style={{ background: 'rgba(180,80,80,0.1)', color: '#8B2020', fontFamily: 'Inter, sans-serif' }}
              >
                {loginError}
              </div>
            )}
            <div className="mb-6">
              <label className="block text-xs tracking-[0.15em] uppercase font-medium mb-2" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
                Username
              </label>
              <input
                className="form-input"
                data-testid="admin-username-input"
                value={loginForm.username}
                onChange={(e) => setLoginForm((f) => ({ ...f, username: e.target.value }))}
                placeholder="admin"
                autoComplete="username"
                required
              />
            </div>
            <div className="mb-8">
              <label className="block text-xs tracking-[0.15em] uppercase font-medium mb-2" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
                Password
              </label>
              <input
                type="password"
                className="form-input"
                data-testid="admin-password-input"
                value={loginForm.password}
                onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="••••••••••"
                autoComplete="current-password"
                required
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="w-full rounded-full text-sm font-medium transition-all duration-300 hover:opacity-85"
              style={{ background: '#7E8C74', color: '#F7F4EF', padding: '14px', fontFamily: 'Inter, sans-serif' }}
            >
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── DASHBOARD ──────────────────────────────────────────────────────────────
  return (
    <div data-testid="admin-dashboard" style={{ background: '#F7F4EF', minHeight: '100vh', paddingTop: '80px' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-medium mb-1" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
              Resonant Healing
            </p>
            <h1 className="font-serif text-2xl sm:text-3xl" style={{ color: '#2B2B2B' }}>Admin Dashboard</h1>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="text-sm rounded-full transition-all duration-200 hover:opacity-75"
            style={{ color: 'rgba(43,43,43,0.55)', fontFamily: 'Inter, sans-serif', padding: '8px 20px', border: '1px solid #EAE3D9' }}
          >
            Sign Out
          </button>
        </div>

        {/* Status message */}
        {statusMsg && (
          <div
            data-testid="status-message"
            className="mb-6 p-4 rounded-2xl text-sm"
            style={{ background: 'rgba(168,181,162,0.2)', border: '1px solid #A8B5A2', color: '#4A5E45', fontFamily: 'Inter, sans-serif' }}
          >
            {statusMsg}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total', value: stats.total, color: '#2B2B2B' },
            { label: 'Pending', value: stats.pending, color: '#8B6B28' },
            { label: 'Reviewed', value: stats.reviewed, color: '#4A5E45' },
            { label: 'Accepted', value: stats.accepted, color: '#2E6B2E' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="p-6 rounded-3xl text-center"
              style={{ background: '#EAE3D9', boxShadow: '0 4px 20px rgba(126,140,116,0.07)' }}
            >
              <p className="font-serif text-3xl font-light mb-1" style={{ color: stat.color }}>{stat.value}</p>
              <p className="text-xs tracking-[0.15em] uppercase" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Google Sheets */}
        <div
          className="p-6 rounded-3xl mb-8"
          style={{ background: '#EAE3D9', boxShadow: '0 4px 20px rgba(126,140,116,0.07)' }}
          data-testid="sheets-section"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-medium mb-1" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
                Google Sheets Integration
              </p>
              <div className="flex items-center gap-2 mt-1">
                <div
                  className="rounded-full"
                  style={{ width: '8px', height: '8px', background: sheetsStatus?.connected ? '#4A8B4A' : '#C0856B' }}
                />
                <span className="text-sm" style={{ color: 'rgba(43,43,43,0.7)', fontFamily: 'Inter, sans-serif' }}>
                  {sheetsStatus?.connected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
              {!sheetsStatus?.connected && (
                <p className="text-xs mt-2" style={{ color: 'rgba(43,43,43,0.5)', fontFamily: 'Inter, sans-serif' }}>
                  Note: Add <code style={{ background: 'rgba(0,0,0,0.07)', padding: '1px 5px', borderRadius: '4px' }}>{`${window.location.origin}/api/oauth/sheets/callback`}</code> to your Google Cloud Console Authorized Redirect URIs first.
                </p>
              )}
            </div>
            <div className="flex gap-3 flex-wrap">
              {sheetsStatus?.connected && (
                <button
                  onClick={handleInitHeaders}
                  data-testid="init-headers-btn"
                  className="text-sm rounded-full transition-all duration-200 hover:opacity-85"
                  style={{ background: '#F7F4EF', color: '#7E8C74', padding: '9px 20px', fontFamily: 'Inter, sans-serif', border: '1px solid #A8B5A2' }}
                >
                  Initialize Headers
                </button>
              )}
              <a
                href={`${API}/oauth/sheets/login`}
                data-testid="connect-sheets-btn"
                className="text-sm rounded-full transition-all duration-200 hover:opacity-85 inline-block text-center"
                style={{ background: sheetsStatus?.connected ? '#EAE3D9' : '#A8B5A2', color: '#2B2B2B', padding: '9px 22px', fontFamily: 'Inter, sans-serif', border: '1px solid #A8B5A2' }}
              >
                {sheetsStatus?.connected ? 'Reconnect' : 'Connect Sheets'}
              </a>
            </div>
          </div>
        </div>

        {/* Application Detail Panel */}
        {selectedApp && (
          <div
            data-testid="application-detail"
            className="mb-8 p-8 rounded-3xl"
            style={{ background: '#EAE3D9', boxShadow: '0 8px 40px rgba(126,140,116,0.1)' }}
          >
            <div className="flex justify-between items-start mb-8">
              <div>
                <h2 className="font-serif text-2xl mb-1" style={{ color: '#2B2B2B' }}>{selectedApp.fullName}</h2>
                <p className="text-sm" style={{ color: 'rgba(43,43,43,0.6)', fontFamily: 'Inter, sans-serif' }}>
                  {selectedApp.preferredName ? `Preferred: ${selectedApp.preferredName} · ` : ''}{selectedApp.email} · {selectedApp.phone}
                </p>
                <p className="text-xs mt-1" style={{ color: 'rgba(43,43,43,0.45)', fontFamily: 'Inter, sans-serif' }}>
                  Submitted: {new Date(selectedApp.submittedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              <button
                onClick={() => setSelectedApp(null)}
                data-testid="close-detail-btn"
                className="text-2xl leading-none ml-4"
                style={{ color: 'rgba(43,43,43,0.35)' }}
              >
                ×
              </button>
            </div>

            <div className="flex flex-wrap gap-3 mb-8">
              <StatusBadge status={selectedApp.status} />
              <select
                data-testid="status-select"
                value={selectedApp.status}
                onChange={(e) => handleStatusUpdate(selectedApp.id, e.target.value)}
                className="text-xs rounded-full px-3 py-1 border outline-none"
                style={{ border: '1px solid #A8B5A2', background: '#F7F4EF', color: '#2B2B2B', fontFamily: 'Inter, sans-serif', cursor: 'pointer' }}
              >
                <option value="pending">Set: Pending</option>
                <option value="reviewed">Set: Reviewed</option>
                <option value="accepted">Set: Accepted</option>
                <option value="declined">Set: Declined</option>
              </select>
              <button
                onClick={() => handleDelete(selectedApp.id)}
                data-testid="delete-application-btn"
                className="text-xs rounded-full px-3 py-1 transition-colors"
                style={{ border: '1px solid rgba(180,80,80,0.3)', color: '#8B2020', fontFamily: 'Inter, sans-serif' }}
              >
                Delete
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-x-12">
              <div>
                <Field label="What's Calling Them" value={selectedApp.whatIsCalling} />
                <Field label="What's Not Sustainable" value={selectedApp.notSustainable} />
                <Field label="Healing History" value={selectedApp.healingHistory} />
                <Field label="Supportive / Not Supportive" value={selectedApp.supportiveNotSupportive} />
              </div>
              <div>
                <Field label="How They Respond" value={selectedApp.howYouRespond} />
                <Field label="What Feeling Safe Looks Like" value={selectedApp.feelingSafe} />
                <Field label="Draws to Container" value={selectedApp.drawsYouToContainer} />
                <Field label="Hoping to Experience" value={selectedApp.hopingToExperience} />
                <Field label="Resource Level" value={selectedApp.resourceLevel} />
                {selectedApp.anythingElse && <Field label="Anything Else" value={selectedApp.anythingElse} />}
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <input
            className="form-input"
            data-testid="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
            style={{ maxWidth: '360px' }}
          />
        </div>

        {/* Applications Table */}
        <div
          className="rounded-3xl overflow-hidden"
          style={{ background: '#EAE3D9', boxShadow: '0 4px 20px rgba(126,140,116,0.07)' }}
          data-testid="applications-table-container"
        >
          {loading ? (
            <div className="text-center py-16" style={{ color: 'rgba(43,43,43,0.5)', fontFamily: 'Inter, sans-serif' }}>
              Loading applications...
            </div>
          ) : filteredApps.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-serif text-xl mb-2" style={{ color: '#2B2B2B' }}>No applications yet</p>
              <p className="text-sm" style={{ color: 'rgba(43,43,43,0.5)', fontFamily: 'Inter, sans-serif' }}>
                Applications submitted through the form will appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="applications-table">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(168,181,162,0.3)' }}>
                    {['Name', 'Email', 'Phone', 'Submitted', 'Status', 'Action'].map((h) => (
                      <th
                        key={h}
                        className="text-left py-4 px-6 text-xs tracking-[0.15em] uppercase font-medium"
                        style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((app, i) => (
                    <tr
                      key={app.id}
                      data-testid={`application-row-${i}`}
                      style={{
                        borderBottom: i < filteredApps.length - 1 ? '1px solid rgba(168,181,162,0.2)' : 'none',
                        background: selectedApp?.id === app.id ? 'rgba(168,181,162,0.12)' : 'transparent',
                      }}
                    >
                      <td className="py-4 px-6">
                        <p className="text-sm font-medium" style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}>{app.fullName}</p>
                        {app.preferredName && (
                          <p className="text-xs mt-0.5" style={{ color: 'rgba(43,43,43,0.5)', fontFamily: 'Inter, sans-serif' }}>
                            {app.preferredName}
                          </p>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <p className="text-sm" style={{ color: 'rgba(43,43,43,0.75)', fontFamily: 'Inter, sans-serif' }}>{app.email}</p>
                      </td>
                      <td className="py-4 px-6">
                        <p className="text-sm" style={{ color: 'rgba(43,43,43,0.75)', fontFamily: 'Inter, sans-serif' }}>{app.phone}</p>
                      </td>
                      <td className="py-4 px-6">
                        <p className="text-xs" style={{ color: 'rgba(43,43,43,0.6)', fontFamily: 'Inter, sans-serif' }}>
                          {new Date(app.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </td>
                      <td className="py-4 px-6">
                        <StatusBadge status={app.status} />
                      </td>
                      <td className="py-4 px-6">
                        <button
                          onClick={() => setSelectedApp(selectedApp?.id === app.id ? null : app)}
                          data-testid={`view-app-btn-${i}`}
                          className="text-xs rounded-full px-3 py-1.5 transition-all duration-200 hover:opacity-85"
                          style={{ background: '#A8B5A2', color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                        >
                          {selectedApp?.id === app.id ? 'Close' : 'View'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;
